import { useCallback, useEffect, useMemo, useState } from "react";
import { useClientes } from "../../../../../hooks/useClientes";
import type { Cliente } from "../../../../../types/gerArquivos";
import { sendToTerminal } from "../../../../../lib/terminalLogger";
import { LogCategory } from "../../../../../config/logging";
import { getTenantId } from "../../../../../utils/getTenantId";
import { requestCrossTenantOperation } from "../../services/crossTenantExchangeService";
import { QuotationOperationContextStore } from "../../services/quotationOperationContext";

export interface UseQuotationClientStateOptions {
	/** Habilita busca/seleção de clientes roteados (cross-tenant). */
	routedMode?: boolean;
	/** Cliente pré-selecionado (ex.: escolhido no gate inicial). */
	initialClient?: Cliente | null;
}

export function useQuotationClientState(
	options?: UseQuotationClientStateOptions,
) {
	// Toggle "cliente de outra unidade" (roteado). Opcional, desligado por padrão.
	const [routedMode, setRoutedMode] = useState<boolean>(
		options?.routedMode ?? false,
	);

	const [selectedClient, setSelectedClient] = useState<Cliente | null>(
		options?.initialClient ?? null,
	);
	const [clientSearchQuery, setClientSearchQuery] = useState("");
	const [manualClientName, setManualClientName] = useState("");
	const [useManualClientName, setUseManualClientName] = useState(false);
	const [routingError, setRoutingError] = useState<string | null>(null);
	const [preparingRouting, setPreparingRouting] = useState(false);

	const {
		clientes,
		carregando: carregandoClientes,
		buscarClientes,
		limparBusca,
	} = useClientes({ includeRouted: routedMode });

	useEffect(() => {
		if (clientSearchQuery.trim().length >= 2) {
			buscarClientes(clientSearchQuery);
		} else {
			limparBusca();
		}
	}, [buscarClientes, clientSearchQuery, limparBusca]);

	const handleSelectClient = useCallback(
		async (client: Cliente) => {
			if (!client?.id) {
				sendToTerminal(
					"[QUOTATION] Tentativa de selecionar cliente invalido",
					{},
					LogCategory.ERROR,
				);
				return;
			}

			setRoutingError(null);

			// Cliente roteado: troca o contexto operacional para o tenant destino
			// ANTES do pipeline, para que regras/preços usem o tenant correto.
			if (client.isRouted && client.targetTenantId) {
				try {
					setPreparingRouting(true);
					const context = await requestCrossTenantOperation({
						targetTenantId: client.targetTenantId,
						targetClientId: Number(client.id),
						routingConfigId: client.routingConfigId ?? 0,
						sourceTenantId: getTenantId(),
					});
					QuotationOperationContextStore.set(context);

					sendToTerminal(
						"[QUOTATION] Contexto de operação roteado ativado",
						{
							targetTenantId: context.effectiveTenantId,
							targetClientId: client.id,
						},
						LogCategory.BUSINESS,
					);
				} catch (error) {
					QuotationOperationContextStore.clear();
					const message =
						error instanceof Error
							? error.message
							: "Falha ao preparar cliente roteado.";
					setRoutingError(message);
					sendToTerminal(
						"[QUOTATION] Falha ao ativar contexto roteado",
						{ error: message },
						LogCategory.ERROR,
					);
					return;
				} finally {
					setPreparingRouting(false);
				}
			} else {
				// Cliente local: garante que nenhum contexto roteado permaneça ativo.
				QuotationOperationContextStore.clear();
			}

			setSelectedClient(client);
			setClientSearchQuery("");
			limparBusca();

			sendToTerminal(
				"[QUOTATION] Cliente selecionado",
				{
					clienteId: client.id,
					clienteNome: client.empresa || client.nome,
					clienteWhatsapp: client.whatsapp || null,
					isRouted: Boolean(client.isRouted),
				},
				LogCategory.BUSINESS,
			);
		},
		[limparBusca],
	);

	const handleClearClient = useCallback(() => {
		QuotationOperationContextStore.clear();
		setSelectedClient(null);
		setClientSearchQuery("");
		setManualClientName("");
		setUseManualClientName(false);
		setRoutingError(null);
		limparBusca();
	}, [limparBusca]);

	const handleToggleManualName = useCallback(
		(enabled: boolean) => {
			setUseManualClientName(enabled);
			if (enabled) {
				setSelectedClient(null);
				setClientSearchQuery("");
				limparBusca();
				sendToTerminal(
					"[QUOTATION] Modo de nome manual ativado",
					{},
					LogCategory.BUSINESS,
				);
			} else {
				setManualClientName("");
				sendToTerminal(
					"[QUOTATION] Modo de busca de cliente ativado",
					{},
					LogCategory.BUSINESS,
				);
			}
		},
		[limparBusca],
	);

	// Alterna o modo "cliente de outra unidade" (roteado). Ao ligar, desativa o
	// nome manual; ao desligar, descarta o contexto roteado e o cliente roteado.
	const handleToggleRoutedMode = useCallback(
		(enabled: boolean) => {
			setRoutedMode(enabled);
			setClientSearchQuery("");
			setRoutingError(null);
			limparBusca();
			if (enabled) {
				setUseManualClientName(false);
				setManualClientName("");
			} else {
				QuotationOperationContextStore.clear();
				setSelectedClient((prev) => (prev?.isRouted ? null : prev));
			}
			sendToTerminal(
				"[QUOTATION] Toggle cliente de outra unidade",
				{ enabled },
				LogCategory.BUSINESS,
			);
		},
		[limparBusca],
	);

	const finalClientName = useMemo(() => {
		if (useManualClientName && manualClientName.trim()) {
			return manualClientName.trim();
		}
		if (selectedClient) {
			return selectedClient.empresa || selectedClient.nome;
		}
		return null;
	}, [manualClientName, selectedClient, useManualClientName]);

	return {
		selectedClient,
		clientSearchQuery,
		manualClientName,
		useManualClientName,
		clientes,
		carregandoClientes,
		finalClientName,
		routedMode,
		routingError,
		preparingRouting,
		setClientSearchQuery,
		setManualClientName,
		handleSelectClient,
		handleClearClient,
		handleToggleManualName,
		handleToggleRoutedMode,
	};
}
